import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Icon from "components/AppIcon";
import toast from "react-hot-toast";

const AVATAR_COLORS = [
  "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  "bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300",
  "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  "bg-pink-100 text-pink-700 dark:bg-pink-900/40 dark:text-pink-300",
  "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
];

/** Accept both backend ("you_owe_vendor"/"vendor_owes_you") and short forms. */
const normalizeBalanceType = (raw) => {
  switch (raw) {
    case "you_owe_vendor":
    case "you_owe":
      return "you_owe";
    case "vendor_owes_you":
    case "owes_you":
      return "owes_you";
    default:
      return "none";
  }
};

const VendorTable = ({ vendors, onEdit, onDelete }) => {
  if (!vendors.length) return null;

  return (
    <div className="bg-card rounded-xl shadow-sm border border-border overflow-visible">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              {[
                "Vendor",
                "Contact",
                "Email",
                "Tax ID",
                "Balance",
                "Actions",
              ].map((h) => (
                <th
                  key={h}
                  className="px-5 py-4 text-left text-sm font-medium text-foreground whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {vendors.map((vendor, index) => (
              <VendorRow
                key={vendor.id}
                vendor={vendor}
                index={index}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const VendorRow = ({ vendor, index, onEdit, onDelete }) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const initials = (vendor.name || "?")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const avatarColor = AVATAR_COLORS[index % AVATAR_COLORS.length];

  const balance = parseFloat(vendor.openingBalance) || 0;
  const direction = normalizeBalanceType(vendor.balanceType);
  const isYouOwe = direction === "you_owe";
  const isVendorOwes = direction === "owes_you";

  // Backend fields (with graceful fallback to legacy names).
  const phone = vendor.phone || vendor.contact || "";
  const vatNumber = vendor.vatNumber || vendor.taxNumber || "";
  const subtitle =
    vendor.displayName && vendor.displayName !== vendor.name
      ? vendor.displayName
      : "";

  const actions = [
    {
      icon: "Edit3",
      label: "Edit Vendor",
      color: "text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20",
      onClick: () => onEdit(vendor),
    },
    {
      icon: "Receipt",
      label: "Expense Against Vendor",
      color: "text-violet-500 hover:bg-violet-50 dark:hover:bg-violet-900/20",
      onClick: () => toast("Expense against vendor — coming soon"),
    },
    {
      icon: "Wallet",
      label: "Advance Payments to Vendor",
      color: "text-teal-500 hover:bg-teal-50 dark:hover:bg-teal-900/20",
      onClick: () => toast("Advance payments — coming soon"),
    },
    {
      icon: "BarChart3",
      label: "Expense Summary",
      color: "text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-900/20",
      onClick: () => toast("Expense summary — coming soon"),
    },
    {
      icon: "Trash2",
      label: "Delete Vendor",
      color: "text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20",
      onClick: () => setShowDeleteConfirm(true),
    },
  ];

  return (
    <>
      <motion.tr
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: index * 0.04 }}
        className="hover:bg-muted/50 transition-colors"
      >
        {/* Vendor */}
        <td className="px-5 py-4">
          <div className="flex items-center gap-3">
            <div
              className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${avatarColor}`}
            >
              {initials}
            </div>
            <div>
              <p className="font-medium text-foreground">{vendor.name}</p>
              {subtitle && (
                <p className="text-xs text-muted-foreground truncate max-w-[160px]">
                  {subtitle}
                </p>
              )}
            </div>
          </div>
        </td>

        {/* Contact */}
        <td className="px-5 py-4 text-sm text-foreground">
          {phone || <span className="text-muted-foreground">—</span>}
        </td>

        {/* Email */}
        <td className="px-5 py-4 text-sm text-foreground">
          {vendor.email || <span className="text-muted-foreground">—</span>}
        </td>

        {/* Tax ID */}
        <td className="px-5 py-4">
          {vatNumber ? (
            <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded text-foreground">
              {vatNumber}
            </span>
          ) : (
            <span className="text-muted-foreground text-sm">—</span>
          )}
        </td>

        {/* Balance */}
        <td className="px-5 py-4">
          {balance > 0 && direction !== "none" ? (
            <div>
              <p
                className={`font-semibold text-sm ${isYouOwe ? "text-red-500" : "text-emerald-600"}`}
              >
                {isYouOwe ? "− " : "+ "}
                SR{" "}
                {balance.toLocaleString("en-SA", { minimumFractionDigits: 2 })}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isYouOwe ? "You Owe" : "Vendor Owes You"}
              </p>
            </div>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </td>

        {/* Actions */}
        <td className="px-5 py-4">
          <div className="flex items-center gap-1">
            {actions.map((action) => (
              <ActionBtn key={action.label} {...action} />
            ))}
          </div>
        </td>
      </motion.tr>

      {/* Inline delete confirmation */}
      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.tr
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <td
              colSpan={6}
              className="px-5 py-3 bg-red-50 dark:bg-red-900/10 border-b border-red-200 dark:border-red-800"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm text-red-700 dark:text-red-300 font-medium">
                  Delete vendor <strong>{vendor.name}</strong>? All associated
                  data will be affected.
                </span>
                <div className="flex gap-2 ml-4 flex-shrink-0">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    className="px-3 py-1.5 text-sm rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      onDelete(vendor.id);
                      setShowDeleteConfirm(false);
                    }}
                    className="px-3 py-1.5 text-sm rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </td>
          </motion.tr>
        )}
      </AnimatePresence>
    </>
  );
};

const ActionBtn = ({ icon, label, color, onClick }) => (
  <div className="relative group">
    <button
      onClick={onClick}
      className={`p-1.5 rounded-lg transition-colors ${color}`}
    >
      <Icon name={icon} size={14} />
    </button>
    <div className="absolute bottom-full mb-1.5 left-1/2 -translate-x-1/2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded-md shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 border border-border">
      {label}
    </div>
  </div>
);

export default VendorTable;
